import React, { useState } from "react";
import { Auth, API } from "aws-amplify";
import AccessDeniedModal from "./AccessDeniedModal";
import { parseCentralDateTime, formatDateInZone } from "../utils/calendarTimezone";
import "./CalendarEntryInfoButton.scss";

const CENTRAL_TIMEZONE = "America/Chicago";

const CalendarEntryInfoButton = ({ classData, startTimeCentralFormatted, endTimeCentralFormatted, startTimeLocalFormatted, endTimeLocalFormatted }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showAccessDeniedModal, setShowAccessDeniedModal] = useState(false);

    const startDateTime = parseCentralDateTime(classData.start_date, classData.start_time);
    const formattedDate = formatDateInZone(startDateTime, CENTRAL_TIMEZONE);

    const handleOpenModal = async () => {
        try {
            const user = await Auth.currentAuthenticatedUser({
                bypassCache: true,
            });
            const token = user.signInUserSession.idToken.jwtToken;

            const myInit = {
                headers: {
                    Authorization: token,
                },
            };

            const response = await API.get(
                "bbwvideoauthapi", // Make sure this API is correctly configured
                "/videoaccess",    // Change the endpoint if needed
                myInit
            );

            if (response !== "Access granted") {
                setShowAccessDeniedModal(true);
                return;
            }

            // If access is granted, open the modal
            setIsModalOpen(true);

        } catch (error) {
            console.log("Error checking class access:", error);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setShowAccessDeniedModal(false); // Close the access denied modal if it was open
    };

    // Function to parse HTML content into React elements
    const parseContent = (content) => {
        if (!content) return null;

        // Remove WordPress shortcodes and clean up the content
        let cleanedContent = content
            .replace(/\[x_button[^\]]*\]/g, '') // Remove x_button shortcodes
            .replace(/\[\/x_button\]/g, '') // Remove closing x_button tags
            .replace(/Join Class/g, '') // Remove any remaining "Join Class" text
            .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
            .replace(/\r\n\r\n/g, '\n\n') // Convert double line breaks to double newlines
            .replace(/\r\n/g, '\n') // Convert single line breaks to newlines
            .trim();

        const parser = new DOMParser();
        const parsedHtml = parser.parseFromString(cleanedContent, "text/html");
        const nodes = Array.from(parsedHtml.body.childNodes);

        return nodes.map((node, index) => {
            if (!node.outerHTML || node.outerHTML.trim() === '') {
                return null;
            }

            // Skip BR elements - we'll handle spacing with CSS
            if (node.nodeName === "BR") {
                return null;
            }

            // Skip empty divs
            if (node.nodeName === "DIV" && (!node.innerHTML || node.innerHTML.trim() === '')) {
                return null;
            }

            // Handle different node types
            if (node.nodeName === "P") {
                return (
                    <p 
                        key={index} 
                        className="modal-paragraph"
                        dangerouslySetInnerHTML={{ __html: node.innerHTML }}
                    />
                );
            }

            if (node.nodeName === "DIV") {
                // Check if this div contains only whitespace or line breaks
                const textContent = node.textContent || '';
                if (textContent.trim() === '') {
                    return null;
                }
                
                // For divs with nested content, split into separate elements for proper spacing
                const innerHTML = node.innerHTML;
                if (innerHTML && innerHTML.trim()) {
                    // Flatten nested divs and split content by double line breaks
                    let flattenedHTML = innerHTML
                        .replace(/<div>/g, '') // Remove opening div tags
                        .replace(/<\/div>/g, '') // Remove closing div tags
                        .replace(/<div><\/div>/g, '') // Remove empty divs
                        .replace(/<div>\s*<\/div>/g, '') // Remove divs with only whitespace
                        .trim();
                    
                    if (flattenedHTML) {
                        // Split content by double line breaks, but keep list introductions with their lists
                        let sections = flattenedHTML.split(/\n\n+/).filter(section => section.trim());
                        
                        // Merge sections that are list introductions with their following lists
                        const mergedSections = [];
                        for (let i = 0; i < sections.length; i++) {
                            const currentSection = sections[i].trim();
                            const nextSection = sections[i + 1] ? sections[i + 1].trim() : '';
                            
                            // If current section doesn't contain a list but next section does,
                            // and current section is short (likely an introduction), merge them
                            if (!currentSection.includes('<ul>') && 
                                !currentSection.includes('<li>') && 
                                nextSection.includes('<ul>') && 
                                currentSection.length < 100) {
                                mergedSections.push(currentSection + '<br><br>' + nextSection);
                                i++; // Skip the next section since we merged it
                            } else {
                                mergedSections.push(currentSection);
                            }
                        }
                        sections = mergedSections;
                        
                        if (sections.length > 1) {
                            // Return multiple divs for proper spacing
                            return sections.map((section, sectionIndex) => {
                                const trimmedSection = section.trim();
                                
                                // Check if this section contains a list
                                if (trimmedSection.includes('<ul>') || trimmedSection.includes('<li>')) {
                                    return (
                                        <div 
                                            key={`${index}-${sectionIndex}`} 
                                            className="modal-content-block"
                                            dangerouslySetInnerHTML={{ __html: trimmedSection }}
                                        />
                                    );
                                } else {
                                    return (
                                        <div 
                                            key={`${index}-${sectionIndex}`} 
                                            className="modal-div"
                                            dangerouslySetInnerHTML={{ __html: trimmedSection }}
                                        />
                                    );
                                }
                            });
                        } else {
                            // Single section, return as is
                            return (
                                <div 
                                    key={index} 
                                    className="modal-div"
                                    dangerouslySetInnerHTML={{ __html: flattenedHTML }}
                                />
                            );
                        }
                    }
                }
                
                return (
                    <div 
                        key={index} 
                        className="modal-div"
                        dangerouslySetInnerHTML={{ __html: node.innerHTML }}
                    />
                );
            }

            if (node.nodeName === "UL") {
                return (
                    <ul 
                        key={index} 
                        className="modal-list"
                        dangerouslySetInnerHTML={{ __html: node.innerHTML }}
                    />
                );
            }

            if (node.nodeName === "SPAN") {
                return (
                    <div 
                        key={index} 
                        className="modal-span"
                        dangerouslySetInnerHTML={{ __html: node.innerHTML }}
                    />
                );
            }

            // Default case for other elements
            return (
                <div 
                    key={index} 
                    className="modal-content-block"
                    dangerouslySetInnerHTML={{ __html: node.outerHTML }} 
                />
            );
        }).filter(Boolean); // Remove null values
    };

    return (
        <>
            <button className="more-info-button" onClick={handleOpenModal}>
                Class Information
            </button>
            {isModalOpen && (
                <div className="modal-overlay" onClick={handleCloseModal}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <button className="modal-close-x" onClick={handleCloseModal}>×</button>
                        <h3>{classData.title}</h3>
                        <p className="modal-date"><strong>Date: </strong> {formattedDate}</p>
                        <div className="modal-text-date-time">
                            {/* Display Central Time */}
                            <p><strong>Time (CT):</strong> {startTimeCentralFormatted} - {endTimeCentralFormatted}</p>
                            {/* Display Local Time */}
                            <p><strong>Time (Your Time):</strong> {startTimeLocalFormatted} - {endTimeLocalFormatted}</p>
                        </div>
                        
                        <div className="modal-text-buttons">
                            {/* Render parsed content */}
                            <div className="modal-content-text">
                                {parseContent(classData.content)}
                            </div>
                        </div>
                        <div className="calendar-modal-buttons">
                            <a href={classData.class_url} target="_blank" rel="noopener noreferrer" className="join-class-modal-button">Join Class</a>
                        </div>
                        
                    </div>
                </div>
            )}

            {showAccessDeniedModal && (
                <AccessDeniedModal onClose={handleCloseModal} />
            )}
        </>
    );
};

export default CalendarEntryInfoButton;
